import React, { FC, useEffect, useRef, useState } from "react";
import { DateTimePickerProps } from "./DateTimePicker.types";
import { DropdownPosition } from "../Dropdown /Dropdown.types";
import Calendar from "react-calendar";
import { useOnClickOutside, useScrollLock } from "../../hooks";
import styles from "./DateTimePicker.module.scss";
import { CIAngleLeft, CIAngleRight, CITimes } from "@dua.design/icons";
import { Button, TextField, Typography } from "../index";

const DateTimePicker: FC<DateTimePickerProps> = ({
  anchorElement,
  includeTime,
  onClose,
  open,
  includeDate,
  defaultValues,
  defaultTimeValues,
  handleSave,
  placement,
}: DateTimePickerProps) => {
  const ref = useRef(null);
  const [position, setPosition] = useState<DropdownPosition>();
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true);
  const [date, setDate] = useState<Array<Date>>(defaultValues);
  const [time, setTime] = useState<{ fromTime: string; toTime: string }>(
    defaultTimeValues
  );

  useOnClickOutside(ref, onClose, [anchorElement], !open);
  useScrollLock(anchorElement && open);

  useEffect(() => {
    if (open) {
      setIsInitialRender(false);
    }
    if (anchorElement && open) {
      const rect = anchorElement.getBoundingClientRect();
      if (placement === "bottom-end") {
        setPosition({ top: rect.top + rect.height, left: rect.right });
      } else {
        setPosition({ left: rect.left, top: rect.bottom });
      }
    }
  }, [open, anchorElement]);
  useEffect(() => {
    setDate(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    setTime(defaultTimeValues);
  }, [defaultTimeValues]);

  const saveAction = (date, time) => {
    if (handleSave) {
      handleSave(date, time);
      onClose();
    }
    setDate(defaultValues);
    setTime({ toTime: "", fromTime: "" });
  };

  const handleClose = () => {
    setDate(defaultValues);
    setTime({ toTime: "", fromTime: "" });
    onClose();
  };

  const splitDate = (value) => {
    if (value) {
      const arr = String(value).split(" ").slice(1, 4);
      [arr[1], arr[0]] = [arr[0], arr[1]];
      return arr.join(" ");
    }
  };

  return (
    !isInitialRender && (
      <div
        className={[
          !anchorElement && styles.modalWrapper,
          !anchorElement && open && styles.open,
        ].join(" ")}
      >
        <div
          ref={ref}
          style={{
            top: anchorElement && position.top,
            left: anchorElement && position.left,
          }}
          className={[
            anchorElement ? styles.dropdownWrapper : styles.subModalWrapper,
            placement === "bottom-end" && styles.bottomEnd,
            open && styles.open,
          ].join(" ")}
        >
          {includeDate && (
            <Calendar
              minDetail="month"
              defaultValues={defaultValues}
              value={date}
              prevLabel={<CIAngleLeft color={"#8F9193"} size={24} />}
              nextLabel={<CIAngleRight color={"#8F9193"} size={24} />}
              selectRange={true}
              next2Label=""
              prev2Label=""
              onChange={(val) => setDate(val)}
              formatShortWeekday={(locale, date) => {
                return date
                  .toLocaleDateString("locale", { weekday: "short" })
                  .slice(0, 2)
                  .toUpperCase();
              }}
              navigationLabel={({ label }) => (
                <Typography variant={"span"} token={200} weight={"medium"}>
                  {label}
                </Typography>
              )}
            />
          )}
          {includeDate && includeTime && <div className={styles.divider} />}
          {includeTime && (
            <div className={styles.timeWrapper}>
              <div>
                <div className={styles.innerTimeWrapper}>
                  <Typography variant={"h6"} token={100} weight={"medium"}>
                    FROM
                  </Typography>
                  <Typography variant={"span"} token={100} weight={"medium"}>
                    {date ? splitDate(date[0]) : "-"}
                  </Typography>
                </div>
                <div className={styles.inputContainer}>
                  <TextField
                    type={"time"}
                    value={time.fromTime}
                    onChange={(e) =>
                      setTime({
                        ...time,
                        fromTime: e.target.value,
                      })
                    }
                    iconAfter={<CITimes />}
                    onIconAfterClick={() => {
                      setTime({ ...time, fromTime: "" });
                    }}
                  />
                </div>
              </div>
              <p className={styles.timeDivider}>-</p>
              <div>
                <div className={styles.innerTimeWrapper}>
                  <Typography variant={"h6"} token={100} weight={"medium"}>
                    TO
                  </Typography>
                  <Typography variant={"span"} token={100} weight={"medium"}>
                    {date ? splitDate(date[1]) : "-"}
                  </Typography>
                </div>
                <div className={styles.inputContainer}>
                  <TextField
                    type={"time"}
                    value={time.toTime}
                    onChange={(e) =>
                      setTime({
                        ...time,
                        toTime: e.target.value,
                      })
                    }
                    iconAfter={<CITimes />}
                    onIconAfterClick={() => {
                      setTime({ ...time, toTime: "" });
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={styles.buttonsWrapper}>
            <Button
              color={"light"}
              variant={"contained"}
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
            <Button
              variant={"outlined"}
              color={"secondary"}
              onClick={() => saveAction(date, time)}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    )
  );
};

export default DateTimePicker;
